import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { on, addClass, selectAll, openModal } from 'helpers/dom'
import { map } from 'helpers/utils'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import './modal-dialog.css'

const selectors = {
  CLOSE_SELECTOR: '.js-close-modal-dialog',
  CONTENT_SELECTOR: '.js-content-modal-dialog',
  OPEN_CLASS: 'modal-dialog-opened',
  OVERLAY_CLASS: 'modal-dialog-overlay',
  VALIDATE_FORM_CLASS: 'validate-form'
}

class ModalDialog extends HTMLElement {
  constructor() {
    super()

    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)
    this._eventsBound = false
    this._hideTimer = null
  }

  connectedCallback() {
    queueMicrotask(() => this.init())
  }

  customDisableBodyScrollLock(targetEl) {
    disableBodyScroll(targetEl, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute('body-scroll-lock-ignore') !== null) {
            return true
          }

          el = el.parentElement
        }
      }
    })
  }

  init() {
    this.closeBtn = this.querySelectorAll(selectors.CLOSE_SELECTOR)
    this.contentEl = this.querySelector(selectors.CONTENT_SELECTOR)
    this.isSlideOut = this.dataset && this.dataset.type === 'slideout'
    this.scrollTarget = this.getAttribute('data-scroll-target')
    this.ensureCloseLabels()
    this.attachEvents()
    this.addOverlay()

    if (!this.classList.contains(selectors.OPEN_CLASS)) {
      this.setAttribute('inert', '')
      this.setAttribute('aria-hidden', 'true')
    }
  }

  ensureCloseLabels() {
    this.closeBtn.forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return

      const hasName =
        (btn.getAttribute('aria-label') || '').trim() ||
        (btn.textContent || '').trim() ||
        btn.getAttribute('aria-labelledby')

      if (!hasName) {
        btn.setAttribute('aria-label', 'Close')
      }
    })
  }

  attachEvents() {
    if (this._eventsBound || !this.contentEl) return

    this.closeBtn.forEach((btn) => {
      on('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.hide()
      }, btn)
    })
    on('click', (event) => {
      if (event.target === this || event.target === this.overlayEl) {
        this.hide()
      }
    }, this)
    this.contentEl && on('click', (e) => e.stopPropagation(), this.contentEl)
    this._eventsBound = true
  }

  onDocumentKeydown(event) {
    if (!this.classList.contains(selectors.OPEN_CLASS)) return

    if (event.key === 'Escape' || event.code === 'Escape') {
      event.preventDefault()
      this.hide()
    }
  }

  focusCloseButton() {
    const closeBtn = this.querySelector(selectors.CLOSE_SELECTOR)

    if (closeBtn instanceof HTMLElement) {
      closeBtn.focus()
    }
  }

  addOverlay() {
    if (this.isSlideOut && !this.overlayEl) {
      this.overlayEl = document.createElement('div')
      addClass(selectors.OVERLAY_CLASS, this.overlayEl)
      this.insertBefore(this.overlayEl, this.contentEl)
    }
  }

  show(target, init = false) {
    if (init || !this.contentEl) this.init()

    if (this._hideTimer) {
      clearTimeout(this._hideTimer)
      this._hideTimer = null
    }
    /** Hide all opened modal before open new modal */

    const allModals = document.querySelectorAll(selectors.OPEN_CLASS)

    allModals.forEach((modal) => modal.hide())

    this.setAttribute('open', '')
    this.setAttribute('aria-hidden', 'false')
    this.removeAttribute('inert')
    this.classList.add(selectors.OPEN_CLASS)
    document.addEventListener('keydown', this.onDocumentKeydown)

    if (!target) {
      const scrollTargetEl = this.scrollTarget ? selectAll(this.scrollTarget, this) : []

      if (scrollTargetEl.length) {
        map(this.customDisableBodyScrollLock, scrollTargetEl)
      } else if (this.contentEl) {
        this.customDisableBodyScrollLock(this.contentEl)
      }
    } else {
      this.customDisableBodyScrollLock(target)
    }

    this.dispatchEvent(new Event('open-modal'))
    // Defer focus so the dialog is visible before moving keyboard focus.
    requestAnimationFrame(() => this.focusCloseButton())

    if (this.isSlideOut) {
      globalEmitter.emit(emitterProps.slideout.open, this.dataset?.target)
    }
  }

  hide() {
    /** Refresh validate form when hide modal */
    const allFormsValidate = this.querySelectorAll(selectors.VALIDATE_FORM_CLASS)

    allFormsValidate.forEach((form) => form.refreshValidate())

    this.removeAttribute('open')
    this.setAttribute('aria-hidden', 'true')
    document.removeEventListener('keydown', this.onDocumentKeydown)
    this.dispatchEvent(new Event('close-modal'))

    // Keep the overlay receiving the current click so it cannot fall through
    // to a plus button / trigger behind the drawer and immediately reopen it.
    if (this._hideTimer) clearTimeout(this._hideTimer)
    this._hideTimer = window.setTimeout(() => {
      this.setAttribute('inert', '')
      this.classList.remove(selectors.OPEN_CLASS)
      this._hideTimer = null
      if (!document.querySelectorAll(`.${selectors.OPEN_CLASS}`).length) clearAllBodyScrollLocks()
    }, 0)

    if (this.isSlideOut) {
      globalEmitter.emit(emitterProps.slideout.close, this.dataset?.target)
    }
  }
}

customElements.define('modal-dialog', ModalDialog)

class ModalOpener extends HTMLElement {
  constructor() {
    super()

    this.idTrigger = this.getAttribute('data-id')
    this.attachEvents()
  }

  attachEvents() {
    this.addEventListener('click', () => {
      if (this.idTrigger === 'account-slideout') {
        globalEmitter.emit(emitterProps.account.show, true)
      }
      openModal(this.idTrigger, false)
    })
    this.addEventListener('keydown', (e) => {
      if (e.keyCode === 13 && !e.shiftKey) {
        openModal(this.idTrigger, false)
      }
    })
  }
}

customElements.define('modal-opener', ModalOpener)
